import axios from "axios";
import i18n from "@/i18n";
import Vue from "vue";
import { Toast } from "vant";
import router from "../router";
import { getToken } from "../utils/auth";
import { getStoredApiBaseUrl } from "./bootstrap";
import { handleAuthError, isPublicApi } from "../utils/permission";

// 1. 全局请求计数器：解决多请求并发时，加载状态被提前关闭的问题
let requestCount = 0;

// 2. 封装加载状态控制函数（统一管理开启/关闭）
const handleLoading = {
  // 开启加载：参数支持自定义文案、是否显示遮罩
  open(options = {}) {
    // 默认配置：文案“加载中...”，显示遮罩（防止用户重复操作）
    const defaultOpts = {
      message: i18n.t("common.loading"),
      overlay: true,
      duration: 0, // 0 表示不自动关闭（需手动关闭）
      forbidClick: true, // 禁止点击遮罩层关闭
    };
    const finalOpts = { ...defaultOpts, ...options };

    // 只有当第一个请求发起时，才显示加载（避免重复显示）
    if (requestCount === 0) {
      Toast.loading(finalOpts);
    }
    requestCount++; // 计数器+1
  },

  // 关闭加载：只有当所有请求都结束时，才关闭加载
  close() {
    if (requestCount > 0) {
      requestCount--; // 计数器-1
    }
    // 当最后一个请求结束，关闭加载
    if (requestCount === 0) {
      Toast.clear(); // 关闭 Vant Toast 加载状态
    }
  },
};

function getApiMessage(data) {
  if (!data) return "";
  if (typeof data === "string") return data;
  return data.message || "";
}

function showApiToast(message) {
  if (message) {
    Toast(message);
  }
}

// 创建 axios 实例（baseURL 在启动页解析远程配置后动态设置）
const service = axios.create({
  baseURL: getStoredApiBaseUrl() || "",
  timeout: 10000,
  // 3. 默认加载配置（可在单个请求中覆盖）
  loading: true, // true：默认显示加载；false：不显示；也可传 { message: '自定义文案' }
});

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 4. 开启加载状态（根据请求配置决定是否显示）
    const loadingConfig = config.loading;
    if (loadingConfig !== false) {
      // 只有明确设置 loading:false 才不显示
      // 若 loading 是对象（如 { message: '提交中...' }），则透传配置；否则用默认
      const loadingOpts =
        typeof loadingConfig === "object" ? loadingConfig : {};
      handleLoading.open(loadingOpts);
    }
    const token = getToken();
    try {
      // 登录、注册及启动配置接口不能携带上一次会话的 Authorization。
      if (token && !isPublicApi(config.url)) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn("请求拦截器添加 token 失败：", error);
    }

    return config;
  },
  (error) => {
    // 5. 请求拦截器报错时，也要关闭加载（避免卡死）
    handleLoading.close();
    console.error("请求拦截器捕获错误：", error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    // 6. 请求成功：关闭加载状态
    handleLoading.close();

    const res = response.data;
    if (response.status !== 200) {
      const message = getApiMessage(res) || getApiMessage(response);
      showApiToast(message);
      return Promise.reject(new Error(message));
    }

    if (res.code !== 200) {
      const message = getApiMessage(res);
      if (res.code === 401 || res.code === 403) {
        if (handleAuthError(router, response.config)) {
          return Promise.reject(new Error(message));
        }
      }
      if (response.config.url !== "huishou") {
        showApiToast(message);
      }
      return Promise.reject(new Error(message));
    }
    return res;
  },
  (error) => {
    handleLoading.close();

    const { response, config } = error;
    const status = response?.status;
    const businessCode = response?.data?.code;

    // 登录态失效：HTTP 401/403 或业务码 401/403
    if (
      (status === 401 || status === 403 || businessCode === 401 || businessCode === 403) &&
      !isPublicApi(config?.url)
    ) {
      if (handleAuthError(router, config)) {
        return Promise.reject(error);
      }
    }

    const message = getApiMessage(response?.data) || error.message || "";
    showApiToast(message);
    return Promise.reject(error);
  }
);

// 8. 封装 GET/POST/PUT/DELETE 请求（保持原有逻辑，支持加载配置透传）
export function get(url, params = {}, config = {}) {
  return service({
    url,
    method: "get",
    params,
    ...config, // 透传配置（如 { loading: false } 或 { loading: { message: '加载数据中...' } }）
  });
}

export function post(url, data = {}, config = {}) {
  return service({
    url,
    method: "post",
    data,
    ...config,
  });
}

export function put(url, data = {}, config = {}) {
  return service({
    url,
    method: "put",
    data,
    ...config,
  });
}

export function del(url, params = {}, config = {}) {
  return service({
    url,
    method: "delete",
    params,
    ...config,
  });
}

export function setRequestBaseURL(url) {
  service.defaults.baseURL = url;
}

// 挂载到 Vue 原型
Vue.prototype.$http = service;

export default service;
